const RELOAD_KEY = "pinkspider_asset_reload_attempted";
const RETRY_DELAY = 300;
const STYLESHEET_TIMEOUT = 8_000;

export class AssetLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AssetLoadError";
  }
}

export function isAssetLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error instanceof AssetLoadError ||
    /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|unable to preload css/i.test(error.message);
}

export function reloadForAssetFailure(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(RELOAD_KEY)) return false;
    sessionStorage.setItem(RELOAD_KEY, "1");
    // Without durable storage, a refresh cannot be safely bounded.
    if (sessionStorage.getItem(RELOAD_KEY) !== "1") return false;
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

export function allowNextAssetRecovery() {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Storage may be blocked. Manual refresh remains available.
  }
}

function recoverOrThrow(error: unknown): Promise<never> {
  if (reloadForAssetFailure()) {
    // Keep the current Suspense content visible until browser navigation
    // completes, rather than committing the error screen before the reload.
    return new Promise<never>(() => {});
  }
  return Promise.reject(error);
}

const delay = () => new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAY));

export function isStylesheetReady(link: HTMLLinkElement): boolean {
  try {
    // A failed native stylesheet request can leave a truthy, empty sheet.
    // Our same-origin application stylesheets always contain real rules.
    return (link.sheet?.cssRules.length ?? 0) > 0;
  } catch {
    return false;
  }
}

function pageRetryUrl(load: () => Promise<unknown>): string | undefined {
  // Vite keeps the static import inside each loader callback. Retry that
  // page's entry, never the failing shared dependency from an error message.
  // A fresh URL avoids the browser's cached, rejected import promise.
  const matches = Array.from(Function.prototype.toString.call(load).matchAll(/\bimport\(\s*["']([^"']+)["']\s*\)/g));
  const entry = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
  if (matches.length !== 1 || !entry) return;
  const url = new URL(matches[0][1], entry);
  if (url.origin !== location.origin || !/^\/assets\/[^/]+\.js$/.test(url.pathname)) return;
  url.searchParams.set("ps_asset_retry", "1");
  return url.href;
}

export async function loadPageModule<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (error) {
    if (!isAssetLoadError(error) || typeof document === "undefined") throw error;
    // If Vite's CSS preloader failed, do not bypass it with a native import.
    // A fresh document must run the normal preload path again.
    const retry = error instanceof Error && !/unable to preload css/i.test(error.message) ?
      pageRetryUrl(load) : undefined;
    if (retry) {
      await delay();
      try {
        return await import(/* @vite-ignore */ retry) as T;
      } catch (retryError) {
        if (!isAssetLoadError(retryError)) throw retryError;
        error = retryError;
      }
    }
    return recoverOrThrow(error);
  }
}

function requestStylesheet(href: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.dataset.recoveredCss = "true";
    const timer = setTimeout(() => finish(false), STYLESHEET_TIMEOUT);
    function finish(success: boolean) {
      clearTimeout(timer);
      link.onload = null;
      link.onerror = null;
      if (success) resolve();
      else {
        link.remove();
        reject(new AssetLoadError("The page stylesheet could not load. Refresh to try again."));
      }
    }
    link.onload = () => finish(isStylesheetReady(link));
    link.onerror = () => finish(false);
    document.head.appendChild(link);
  });
}

export async function loadStylesheet(href: string): Promise<void> {
  try {
    await requestStylesheet(href);
  } catch {
    await delay();
    const retry = new URL(href, location.href);
    retry.searchParams.set("ps_asset_retry", "1");
    try {
      await requestStylesheet(retry.href);
    } catch (error) {
      return recoverOrThrow(error);
    }
  }
}
